import {
  faceGenerationStatus,
  type SolarGenerationDimensions,
} from '@payaca/helpers/solarGenerationAvailability';
import {
  analysisYieldPerPanelByFace,
  solarAnalysedRoofLostRecord,
} from '@payaca/helpers/solarAnalysisYield';
import { type EditPanel, type WorkingFace } from './types';

/**
 * Satellite-estimate generation for an analysed layout whose panels were
 * repacked since the analysis (first layout or Repack): each included panel
 * on an analysed roof is worth that roof's analysis yield per panel. Roofs
 * whose recorded result still matches keep their own figure. Null when any
 * included roof has neither (unavailable or unreadable provenance, a new
 * roof, no analysis yield): the total never covers only part of the system.
 */
export const analysisGenerationEstimate = (args: {
  faces: ReadonlyArray<WorkingFace>;
  panels: ReadonlyArray<EditPanel>;
  dimensions: SolarGenerationDimensions;
  analysisPanels:
    | ReadonlyArray<{ faceIndex: number; annualAcKwh: number }>
    | null
    | undefined;
  /**
   * How many faces the analysis indexes. A kept analysed roof beyond them
   * whose record a save lost (an undone Start over) is still a repacked
   * analysed roof, as the server treats it.
   */
  analysisFaceCount?: number;
}): { annualKwh: number; byFace: Map<number, number> } | null => {
  const yieldPerPanel = analysisYieldPerPanelByFace(args.analysisPanels);
  if (!yieldPerPanel) return null;
  const byFace = new Map<number, number>();
  let annualKwh = 0;
  let estimated = false;
  for (const face of args.faces) {
    const onFace = args.panels.filter((panel) => panel.faceId === face.id);
    const included = onFace.filter((panel) => panel.included);
    if (included.length === 0) {
      byFace.set(face.id, 0);
      continue;
    }
    const status = faceGenerationStatus(face, onFace, args.dimensions);
    let faceKwh: number | null = null;
    if (status === 'valid' || status === 'legacy_unverified') {
      faceKwh = included.reduce((sum, panel) => sum + panel.annualAcKwh, 0);
    } else if (
      (status === 'needs_recalculation' && !face.isNew) ||
      (status === 'unavailable' &&
        args.analysisFaceCount != null &&
        solarAnalysedRoofLostRecord(face, {
          indexesFace: face.id < args.analysisFaceCount,
          hasPanels: !!args.analysisPanels?.length,
        }))
    ) {
      const perPanel = yieldPerPanel(face.id);
      if (perPanel != null) {
        faceKwh = included.length * perPanel;
        estimated = true;
      }
    }
    if (faceKwh == null) return null;
    byFace.set(face.id, faceKwh);
    annualKwh += faceKwh;
  }
  // Nothing was repacked: the recorded results already speak for themselves.
  return estimated ? { annualKwh, byFace } : null;
};
